/**
 * Plan-bake dispatch — the whole "start a bake and stamp it durably" dance,
 * extracted from PlansTicket so the anchor flow can auto-bake the companion
 * plan (WP-A: on the converged path, finishing the anchors IS the "placement
 * is right" declaration — asking for a Bake click after that is asking twice).
 *
 * The fingerprint choreography here is battle-tested — see the plan-bake
 * phase history before changing it:
 *  - lastBaked is recorded at DISPATCH (the user may keep editing mid-job;
 *    staleness must measure against what the job was actually sent);
 *  - previousBaked preserves the currently-serving generation so a failed
 *    re-bake can roll back instead of demoting the plan to the proxy;
 *  - the post-flush re-stamp covers the no-_version window where the first
 *    optimistic write could not PATCH.
 */

import { useProjectStore } from '../stores/projectStore';
import { requestPlanBake, PLAN_BAKE_DEFAULT_DPI } from '../services/tileApi';
import { bakeHash, getBakeState } from './planBake';
import * as transformModule from '../domain/layout/transform';
import type { Overlay } from '../stores/projectStore';

export type BakeDispatchResult =
  | 'started'
  | 'skipped-fresh'      // already baked at the current placement/params
  | 'skipped-busy'       // a bake is already generating
  | 'skipped-unavailable'; // no siteId / no bake params / no source PDF

/**
 * Start a full-resolution bake for a plan overlay. Throws on dispatch
 * failure (callers own the error notification); returns a skip reason when
 * there is nothing to do.
 */
export async function dispatchPlanBake(overlayId: string): Promise<BakeDispatchResult> {
  const st = useProjectStore.getState();
  const siteId = st.meta.siteId;
  const overlay = st.data.overlays.find((o) => o.id === overlayId);
  if (!siteId || !overlay?.bake || !overlay.sourceR2Key) return 'skipped-unavailable';
  const bakeState = getBakeState(overlay);
  if (bakeState === 'baking') return 'skipped-busy';
  if (bakeState === 'baked') return 'skipped-fresh';

  const result = await requestPlanBake(siteId, overlay, {
    pageIndex: overlay.bake.pageIndex,
    pageWidthPt: overlay.bake.pageWidthPt,
    pageHeightPt: overlay.bake.pageHeightPt,
    dpi: overlay.bake.dpi || PLAN_BAKE_DEFAULT_DPI,
    whiteThreshold: overlay.bake.whiteThreshold,
    // The user's crop and erased areas. Omitting them is not a degraded
    // bake, it is the OLD bug: the server would re-render the whole sheet
    // and squeeze it into the cropped placement, and erased areas would
    // come back. Anything added to PlanBake that affects pixels has to be
    // listed here too — the server takes only what it is sent.
    cropRect: overlay.bake.cropRect,
    erasePolygons: overlay.bake.erasePolygons,
  });

  const lastBaked = { hash: bakeHash(overlay), tilesetId: result.tilesetId };
  const freshOverlay = () =>
    useProjectStore.getState().data.overlays.find((o) => o.id === overlay.id);
  const preStamp = freshOverlay();
  const previousBaked = (preStamp?.bake ?? overlay.bake).lastBaked;
  const hadVersion = preStamp?._version != null;
  const closureBake = overlay.bake;
  const stampStarted = () => {
    const base = freshOverlay()?.bake ?? closureBake;
    useProjectStore.getState().updateOverlay(overlay.id, {
      tilesetId: result.tilesetId,
      tileStatus: 'generating' as const,
      bake: {
        ...base,
        needsRebake: undefined, // this attempt supersedes any earlier rollback
        previousBaked,
        lastBaked,
      },
    });
  };
  stampStarted();

  // The job is ALREADY running on the server, so losing the tilesetId here
  // would orphan its COG, marker and tileset row until the watchdog expires.
  try {
    await useProjectStore.getState().flushPendingEntityWrites();
    const after = freshOverlay();
    if (after && (!hadVersion || after.tilesetId !== result.tilesetId)) {
      console.warn(`[PlanBake] bake-start write did not persist for ${overlay.id}; re-applying`);
      stampStarted();
    }
  } catch (err) {
    console.warn('[PlanBake] could not confirm bake-start persistence:', err);
  }
  return 'started';
}

/**
 * The three states a layout's companion link can be in. `ghost` is not
 * hypothetical: `addOverlay` creates the D1 row fire-and-forget and rolls the
 * overlay silently back out of the store if that create fails, while the
 * layout keeps the id it was stamped with at import. A Tel Yitzhak import on
 * 2026-08-30 ended up exactly there — layout linked to
 * `overlay_1788094166575_njexd8`, which never reached the database — and
 * because every consumer only asks "does this resolve?", the answer was a
 * silent no on every later Done: no placement, no bake, no DXF re-align.
 * Distinguishing `ghost` from `none` is what makes the repair safe: a layout
 * that never had a companion must NOT adopt whatever plan appears next (it may
 * be a different sheet), but one whose companion is provably missing should.
 */
export type CompanionLink =
  | { state: 'none' }
  | { state: 'live'; overlay: Overlay }
  | { state: 'ghost'; id: string };

export function companionLink(layout: { planOverlayId?: string } | undefined): CompanionLink {
  const id = layout?.planOverlayId;
  if (!id) return { state: 'none' };
  const overlay = useProjectStore.getState().data.overlays.find((o) => o.id === id);
  return overlay ? { state: 'live', overlay } : { state: 'ghost', id };
}

/** The overlay a layout's anchors should auto-bake, or null. */
export function companionOverlayFor(layout: { planOverlayId?: string } | undefined): Overlay | null {
  const link = companionLink(layout);
  return link.state === 'live' ? link.overlay : null;
}

export type AdoptResult = 'placed-and-baking' | 'placed' | 'linked';

/**
 * Point the active layout's companion link at `overlayId` and run the rest of
 * the converged tail — place the sheet from the anchors, then bake. This is
 * the repair for a ghost link, and it makes the manual fallback ("import the
 * plan by itself") converge on the same end state as the one-upload path
 * instead of staying manual forever.
 *
 * `linked` means the link was repaired but the layout has no transform yet —
 * correct and expected when the plan is imported BEFORE anchoring; the anchor
 * session's Done then places and bakes it.
 */
export async function adoptCompanionOverlay(overlayId: string): Promise<AdoptResult> {
  useProjectStore.getState().setLayoutPlanOverlay(overlayId);
  const placed = await syncCompanionPlacement();
  if (!placed) return 'linked';
  try {
    return (await dispatchPlanBake(overlayId)) === 'started' ? 'placed-and-baking' : 'placed';
  } catch (err) {
    console.warn('[PlanAdopt] bake after adoption failed:', err);
    return 'placed';
  }
}

/**
 * Position the companion plan from the layout's anchor transform
 * (pageImageCorners). Called from BOTH directions of the creation/anchoring
 * race: per-anchor updates AND companion-creation completion AND the anchor
 * session's Done — because the first production smoke proved the obvious
 * ordering wrong: the companion is created async after the import modal
 * closes, and an owner who anchors immediately finishes before the overlay
 * exists, leaving the sheet stranded at its default center placement with
 * nothing left to re-sync it. Returns true when bounds were applied.
 */
export async function syncCompanionPlacement(): Promise<boolean> {
  const st = useProjectStore.getState();
  const layout = st.data.siteLayout;
  if (!layout?.planOverlayId || !layout.transform || !layout.pageImage) {
    if (layout?.planOverlayId) {
      console.warn('[PlanSync] skipped: layout has no', layout.transform ? 'pageImage' : 'transform');
    }
    return false;
  }
  const overlay = st.data.overlays.find((o) => o.id === layout.planOverlayId);
  if (!overlay) {
    console.warn('[PlanSync] skipped: companion overlay not found', layout.planOverlayId);
    return false;
  }
  const { pageImageCorners } = transformModule;
  const bounds = pageImageCorners(
    layout.pageImage.pageWidthPt,
    layout.pageImage.pageHeightPt,
    layout.transform,
  );
  const intended = JSON.stringify(bounds);

  // Durable write — the same dance the bake-start stamp needs, for the same
  // reason: the companion overlay has a busy early life (create, DXF stamp,
  // ingest-ready stamp, per-anchor writes) and a version race can make the
  // PATCH fail; the failure path rolls the OPTIMISTIC bounds back, which on
  // the first Gadot smoke silently stranded the sheet at center placement
  // while the bake hashed the anchored bounds ("Moved since bake" chip).
  // Verify after a flush and re-apply, up to twice, and say so when beaten.
  for (let attempt = 0; attempt < 2; attempt++) {
    useProjectStore.getState().updateOverlay(overlay.id, { bounds });
    try {
      await useProjectStore.getState().flushPendingEntityWrites();
    } catch (err) {
      console.warn('[PlanSync] flush failed:', err);
    }
    const live = useProjectStore.getState().data.overlays.find((o) => o.id === overlay.id);
    if (live && JSON.stringify(live.bounds) === intended) return true;
    console.warn(`[PlanSync] bounds write did not persist (attempt ${attempt + 1}); re-applying`);
  }
  console.warn('[PlanSync] placement sync FAILED after retries — sheet may be mis-placed');
  return false;
}
